import {
  GENERATE_CFDI_SUBSCRIBE_CHECKOUT_SESSION,
  GET_SET_COMPANY_ID_v2,
} from '../utils/api';
import { getData, postData, putData } from '../utils/api-service';
import WashdayClientSingleton from './WashdayClientSingleton';
import { resolveGarmentAttributeCatalogs } from '../utils/garmentAttributeCatalogs';
import {
  ApplySubscriptionPlanChangeRequest,
  CFDIAddonToggleRequest,
  GetAvailableBillingExtraPackagesRequest,
  GetAvailableSubscriptionPlansRequest,
  GetCFDIAddonStatusRequest,
  PreviewSubscriptionPlanChangeRequest,
  UpdateGarmentAttributeCatalogsRequest,
} from '../interfaces/Company';
import { buildStripeReturnUrlOptions } from '../utils/domainReturnUrls';
import type { StripeReturnUrlOptions } from '../utils/domainReturnUrls';
import type {
  CatalogConsolidationApplyRequest,
  CatalogConsolidationPreviewRequest,
} from 'washday-sdk';

type CompaniesSdkWithGarmentAttributeCatalogs = {
  getGarmentAttributeCatalogs?: (companyId: string) => Promise<any>;
  updateGarmentAttributeCatalogsById?: (
    companyId: string,
    data: UpdateGarmentAttributeCatalogsRequest,
  ) => Promise<any>;
};

type CompaniesSdkWithCFDIAddon = {
  getCFDIAddonStatus?: (data: GetCFDIAddonStatusRequest) => Promise<any>;
  enableCFDIAddon?: (data: CFDIAddonToggleRequest) => Promise<any>;
  disableCFDIAddon?: (data: CFDIAddonToggleRequest) => Promise<any>;
};

type CompaniesSdkWithSubscriptionPlanChange = {
  getAvailableSubscriptionPlans?: (data: GetAvailableSubscriptionPlansRequest) => Promise<any>;
  getAvailableBillingExtraPackages?: (data: GetAvailableBillingExtraPackagesRequest) => Promise<any>;
  previewSubscriptionPlanChange?: (data: PreviewSubscriptionPlanChangeRequest) => Promise<any>;
  changeSubscriptionPlan?: (data: ApplySubscriptionPlanChangeRequest) => Promise<any>;
};

type BaseCompaniesSdk = ReturnType<typeof WashdayClientSingleton.getInstance>['companies'];

const getCompaniesSdk = () =>
  WashdayClientSingleton.getInstance().companies as BaseCompaniesSdk &
    CompaniesSdkWithGarmentAttributeCatalogs &
    CompaniesSdkWithCFDIAddon &
    CompaniesSdkWithSubscriptionPlanChange;

export const previewCatalogConsolidationV2 = async (
  companyId: string,
  data: CatalogConsolidationPreviewRequest,
) => WashdayClientSingleton.getInstance().companies.previewCatalogConsolidation(companyId, data);

export const applyCatalogConsolidationV2 = async (
  companyId: string,
  data: CatalogConsolidationApplyRequest,
) => WashdayClientSingleton.getInstance().companies.applyCatalogConsolidation(companyId, data);

export const getCompanyByIdV2 = async (companyId: string) => {
  try {
    return await WashdayClientSingleton.getInstance().companies.getCompanyById(companyId);
  } catch (e) {
    console.log('ERROR getCompanyByIdV2', e);
    throw e;
  }
};

export const getSubscriptionBillingStatus = (companyId: string, options?: { signal?: AbortSignal }) =>
  WashdayClientSingleton.getInstance().companies.getSubscriptionBillingStatus({ companyId }, options);

export const getGarmentAttributeCatalogsV2 = async (companyId: string) => {
  try {
    const response = await getCompaniesSdk().getGarmentAttributeCatalogs!(companyId);
    return {
      ...response,
      data: {
        ...response?.data,
        data: {
          ...response?.data?.data,
          garmentAttributeCatalogs: resolveGarmentAttributeCatalogs(
            response?.data?.data?.garmentAttributeCatalogs,
          ),
        },
      },
    };
  } catch (e) {
    console.log('ERROR getGarmentAttributeCatalogsV2', e);
    throw e;
  }
};

export const updateCompanyLogoByIdV2 = async (companyId: string, data: any) => {
  try {
    const res = await WashdayClientSingleton.getInstance().companies.updateCompanyLogoById(companyId, data);
    return res;
  } catch (e) {
    console.log('ERROR updateCompanyLogoById', e);
    throw e;
  }
};

export const updateCompanyByIdV2 = async (companyId: string, data: any) => {
  try {
    const res = await WashdayClientSingleton.getInstance().companies.updateCompanyById(companyId, data);
    return res;
  } catch (e) {
    console.log('ERROR getCompanyByIdV2', e);
    throw e;
  }
};

export const updateCompanyTaxInfoByIdV2 = async (companyId: string, data: any) => {
  try {
    const res = await WashdayClientSingleton.getInstance().companies.updateCompanyTaxInfoById(companyId, data);
    return res;
  } catch (e) {
    console.log('ERROR updateCompanyTaxInfoByIdV2', e);
    throw e;
  }
};

export const updateCompanySubscriptionBillingInfoByIdV2 = async (companyId: string, data: any) => {
  try {
    return await WashdayClientSingleton.getInstance().companies.updateCompanySubscriptionBillingInfoById(companyId, data);
  } catch (e) {
    console.log('ERROR updateCompanySubscriptionBillingInfoByIdV2', e);
    throw e;
  }
};

export const updateGarmentAttributeCatalogsByIdV2 = async (
  companyId: string,
  data: UpdateGarmentAttributeCatalogsRequest,
) => {
  try {
    return await getCompaniesSdk().updateGarmentAttributeCatalogsById!(companyId, data);
  } catch (e) {
    console.log('ERROR updateGarmentAttributeCatalogsByIdV2', e);
    throw e;
  }
};

export const updateCompanyTaxInfoCertificates = async (companyId: string, data: any) => {
  try {
    const res = await WashdayClientSingleton.getInstance().companies.updateCompanyTaxInfoCertificates(companyId, data);
    return res;
  } catch (e) {
    console.log('ERROR updateCompanyTaxInfoCertificates', e);
    throw e;
  }
};

export const updateCFDIOrgLogo = async (companyId: string, data: any) => {
  try {
    const res = await WashdayClientSingleton.getInstance().companies.updateCFDIOrgLogo(companyId, data);
    return res;
  } catch (e) {
    console.log('ERROR updateCFDIOrgLogo', e);
    throw e;
  }
};

/**
 * @deprecated Legacy standalone CFDI checkout. New activations use the company CFDI add-on APIs.
 */
export const getStripeCheckoutCFDISessionV2 = async (
  data: Partial<StripeReturnUrlOptions> = {},
) => {
  try {
    const res = await WashdayClientSingleton.getInstance().stripe.createCFDISuscrptionCheckoutSession({
      ...buildStripeReturnUrlOptions(),
      ...data,
    });
    return res;
  } catch (e) {
    console.log('ERROR createCFDISuscrptionCheckoutSession', e);
    throw e;
  }
};

export const getStripeCustomerPortalSessionV2 = async (
  data: Partial<StripeReturnUrlOptions> = {},
) => {
  try {
    const res = await WashdayClientSingleton.getInstance().stripe.createCustomerPortalSession({
      ...buildStripeReturnUrlOptions(),
      ...data,
    });
    return res;
  } catch (e) {
    console.log('ERROR createCFDISuscrptionCheckoutSession', e);
    throw e;
  }
};


/**
 * @deprecated Legacy standalone CFDI checkout. New activations use the company CFDI add-on APIs.
 */
export const getStripeCheckoutCFDISession = async () => {
  try {
    const res = await postData(`${GENERATE_CFDI_SUBSCRIBE_CHECKOUT_SESSION}`, {}, null);
    if (!res) {
      throw 'Error al enviar, contacte a soporte tecnico';
    }
    return res;
  } catch (e) {
    console.log('Error al enviar, contacte a soporte tecnico', e);
    throw e;
  }
};

export const updateCompanyInfo = async (companyID, putObj, t = () => { }) => {
  try {
    const res = await putData(
      `${GET_SET_COMPANY_ID_v2}/${companyID}`,
      putObj,
      false,
      t('alerts.success'),
      'multipart/form-data',
      t
    );
    return res;
  } catch (e) {
    console.log('Error al enviar, contacte a soporte tecnico', e);
    throw e;
  }
};

export const getCompanyById = async (companyID) => {
  try {
    const res = await getData(`${GET_SET_COMPANY_ID_v2}/${companyID}`);
    let company = res?.data?.data;
    if (!company) {
      throw 'Error al enviar, contacte a soporte tecnico';
    }
    return company;
  } catch (e) {
    console.log('Error al enviar, contacte a soporte tecnico', e);
    throw e;
  }
};

export const updateCompanyLogo = async (companyID, bodyFormData) => {
  try {
    const res = await putData(
      `${GET_SET_COMPANY_ID_v2}/${companyID}`,
      bodyFormData,
      false,
      null,
      'multipart/form-data'
    );
    if (!res) {
      throw 'Error al enviar, contacte a soporte tecnico';
    }
    return res;
  } catch (e) {
    console.log('Error al enviar, contacte a soporte tecnico', e);
    throw e;
  }
};

export const updateCompanyById = async (companyID, putObj, t = () => { }) => {
  try {
    const res = await putData(
      `${GET_SET_COMPANY_ID_v2}/${companyID}`,
      putObj,
      false,
      t('alerts.success'),
      'multipart/form-data',
      t
    );
    return res;
  } catch (e) {
    console.log('Error al enviar, contacte a soporte tecnico', e);
    throw e;
  }
};


export const enableBillingOverage = async (data: {
  companyId: string;
}) => {
  try {
    return await WashdayClientSingleton.getInstance().companies.enableBillingOverage(data);
  } catch (e) {
    console.log('ERROR enableBillingOverage', e);
    throw e;
  }
};

export const disableBillingOverage = async (data: {
  companyId: string;
}) => {
  try {
    return await WashdayClientSingleton.getInstance().companies.disableBillingOverage(data);
  } catch (e) {
    console.log('ERROR disableBillingOverage', e);
    throw e;
  }
};

export const getCFDIAddonStatus = async (
  data: GetCFDIAddonStatusRequest,
): Promise<any> => {
  try {
    const res = await getCompaniesSdk().getCFDIAddonStatus!(data);
    return res;
  } catch (e) {
    console.log('ERROR getCFDIAddonStatus', e);
    throw e;
  }
};

export const enableCFDIAddon = async (
  data: CFDIAddonToggleRequest,
): Promise<any> => {
  try {
    const res = await getCompaniesSdk().enableCFDIAddon!(data);
    return res;
  } catch (e) {
    console.log('ERROR enableCFDIAddon', e);
    throw e;
  }
};

export const disableCFDIAddon = async (
  data: CFDIAddonToggleRequest,
): Promise<any> => {
  try {
    const res = await getCompaniesSdk().disableCFDIAddon!(data);
    return res;
  } catch (e) {
    console.log('ERROR disableCFDIAddon', e);
    throw e;
  }
};

export const getAvailableSubscriptionPlans = async (
  data: GetAvailableSubscriptionPlansRequest,
): Promise<any> => {
  try {
    const res = await getCompaniesSdk().getAvailableSubscriptionPlans!(data);
    return res;
  } catch (e) {
    console.log('ERROR getAvailableSubscriptionPlans', e);
    throw e;
  }
};

export const getAvailableBillingExtraPackages = async (
  data: GetAvailableBillingExtraPackagesRequest,
): Promise<any> => {
  try {
    const companiesSdk = getCompaniesSdk();
    if (typeof companiesSdk.getAvailableBillingExtraPackages === 'function') {
      return await companiesSdk.getAvailableBillingExtraPackages(data);
    }

    const query = data.companyId
      ? `?companyId=${encodeURIComponent(data.companyId)}`
      : '';
    return await getData(`api/billing/extra-packages${query}`);
  } catch (e) {
    console.log('ERROR getAvailableBillingExtraPackages', e);
    throw e;
  }
};

export const previewSubscriptionPlanChange = async (
  data: PreviewSubscriptionPlanChangeRequest,
): Promise<any> => {
  try {
    const res = await getCompaniesSdk().previewSubscriptionPlanChange!(data);
    return res;
  } catch (e) {
    console.log('ERROR previewSubscriptionPlanChange', e);
    throw e;
  }
};

export const changeSubscriptionPlan = async (
  data: ApplySubscriptionPlanChangeRequest,
): Promise<any> => {
  try {
    const res = await getCompaniesSdk().changeSubscriptionPlan!(data);
    return res;
  } catch (e) {
    console.log('ERROR changeSubscriptionPlan', e);
    throw e;
  }
};

export const getCompanyOrdersMetrics = async () => {
  try {
    return await WashdayClientSingleton.getInstance().companies.getCompanyOrdersMetrics();
  } catch (e) {
    console.log('ERROR getCompanyOrdersMetrics', e);
    throw e;
  }
};
